import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCheckIcon, SettingsIcon } from 'lucide-react';
import { NotificationItem } from '../components/NotificationItem';
import { PageHeader } from '../components/PageHeader';
import { StateScreen } from '../components/StateScreen';
import { SectionTitle } from '../components/ui/Card';
import { healthNotifications, socialNotifications } from '../data/notifications';
import { useLocale } from '../contexts/LocaleContext';
import { useSettings } from '../contexts/SettingsContext';
import { useEngagement } from '../contexts/EngagementContext';
import { useProgress } from '../contexts/ProgressContext';
import { engagementConfig } from '../data/engagement';
import type { NotificationEntry } from '../types';
export function Notifications() {
  const {
    t
  } = useLocale();
  const navigate = useNavigate();
  const settings = useSettings();
  const engagement = useEngagement();
  const progress = useProgress();
  // Streak-risk copy must respect Streak Protection: never say the streak ends
  // while an available protection could preserve it.
  const protectionAvailable = engagement.protectionCharges > 0 && progress.streak >= engagementConfig.streakProtection.minStreakDays;
  const all = [...healthNotifications, ...socialNotifications];
  const withRead = (entry: NotificationEntry): NotificationEntry => ({
    ...entry,
    text: protectionAvailable && entry.protectedText ? entry.protectedText : entry.text,
    unread: entry.unread && !settings.isRead(entry.id)
  });
  const isHealth = (entry: NotificationEntry) => healthNotifications.some(item => item.id === entry.id);
  const today = all.filter(entry => entry.hoursAgo < 24).map(withRead);
  const earlier = all.filter(entry => entry.hoursAgo >= 24).map(withRead);
  const unread = [...today, ...earlier].filter(entry => entry.unread).length;
  if (all.length === 0) {
    return <StateScreen tag={t('state.no-notifications.tag')} title={t('state.no-notifications.title')} body={t('state.no-notifications.body')} data-id="element-3341" data-mp="25oqh7l7kix1z" />;
  }
  const section = (title: string, list: NotificationEntry[]) => {
    if (list.length === 0) return null;
    const health = list.filter(isHealth);
    const social = list.filter(entry => !isHealth(entry));
    return <section className="mt-3 first:mt-0" data-id="element-3342" data-mp="22glmk0z4hw3t">
        <SectionTitle data-id="element-3343" data-mp="22hn8j0hog30b">{title}</SectionTitle>
        {health.length > 0 && <div className="rounded-card border border-gold/35 bg-white/70 px-2" data-id="element-3344" data-mp="1eqht2mfnfcqj">
            <p className="bee-overline pt-2 text-[8.5px] font-bold text-gold-deep" data-id="element-3345" data-mp="19ry4sfdl1ov0">
              {t('notifications.health')}
            </p>
            <ul data-id="element-3346" data-mp="1olszmhgwldn7">
              {health.map(entry => <NotificationItem key={entry.id} entry={entry} priority data-id="element-3347" data-mp="1jo9lrio4b2vs" />)}
            </ul>
          </div>}
        {social.length > 0 && <ul className={`rounded-card border border-sand-line bg-white/70 px-2 ${health.length > 0 ? 'mt-2' : ''}`} data-id="element-3348" data-mp="2v4hq9pn40oa3">
            {social.map(entry => <NotificationItem key={entry.id} entry={entry} data-id="element-3349" data-mp="37nd5h79470xx" />)}
          </ul>}
      </section>;
  };
  return <div className="px-2 pb-2" data-id="element-3350" data-mp="2ton59ir00l5c">
      <PageHeader title={t('notifications.title')} subtitle={t('notifications.unreadCount', {
      n: unread
    })} showBack action={<button type="button" onClick={() => navigate('/config/notificacoes')} aria-label={t('settings.notifications')} className="grid h-11 w-11 shrink-0 place-items-center rounded-pill text-muted transition-colors duration-150 ease-brand hover:text-deep" data-id="element-3352" data-mp="3lfjmevk3dbyf">
            <SettingsIcon size={18} data-id="element-3353" data-mp="3glozzkd3qivn" />
          </button>} data-id="element-3351" data-mp="2tpf3l1bzcwzb" />

      {unread > 0 && <button type="button" onClick={settings.markAllRead} className="mb-2 flex min-h-[38px] items-center gap-1 rounded-pill border border-sand-line bg-white/70 px-2 text-[12px] font-medium text-deep transition-colors duration-150 ease-brand hover:border-deep/30" data-id="element-3354" data-mp="86f5axbjkfxw">
          <CheckCheckIcon size={14} aria-hidden="true" data-id="element-3355" data-mp="2gihkr24k84al" />
          {t('notifications.markAll')}
        </button>}

      {section(t('notifications.today'), today)}
      {section(t('notifications.earlier'), earlier)}

      <p className="mt-2 px-1 text-[11px] leading-snug text-muted" data-id="element-3356" data-mp="345zbqvg02yci">
        {t('notifications.healthHint')}
      </p>
    </div>;
}